import type { ExecutionerStepState } from './executioner_weapon'
import type { FxEvent } from './types'

// executioner_weapon.ts から読まれるので Boss 型は引かない (types.ts → executioner_weapon.ts の循環を作らない)。
type StepHolder = { y: number; exec?: { step: ExecutionerStepState | null } | null }

// 介錯の踏み込み・後ろ跳びの重さ (2026-10-19・検収パック 1021 の E-2 ユーザー「軽く滑って見える」)。
//   今の版: 踏み込みは沈み (crouch 150ms) の間も絵が止まったまま、走りは一枚の絵が床を滑るだけ、着地は横へ 8px 行き過ぎて戻るだけ。
//     後ろ跳びは溜めも着地もなく、等速で 33〜200ms 滑って止まる。縦の動きが1つもないので、重さより速さが勝つ。
//   1021 版:
//     - 踏み込みの沈みで体が縮み (膝を溜める)、走る間は低いまま、着いた瞬間に一番潰れて (体重が乗る) 戻る。
//     - 蹴り出しと着地に土煙、着地の滑りに床の擦り跡。
//     - 後ろ跳びは蹴った瞬間が一番速く、着くまでに減速する (今は等速)。着いた所で潰れて土煙。
//   位置が変わるのは後ろ跳びの途中の速さだけ (出発点・着く点・時間は同じ)。2026-10-19 検収パック 1022 で「直した版の方が良い」→ 配布版へ。前の版＝DEV ?stepWeight=now。
//   研究 = docs/research/2026-10-19-awoken-executioner-1021-fixes.md
export type StepWeight = 'now' | '1021'
let mode: StepWeight = '1021'
export function setStepWeight(raw: string | null): void { mode = import.meta.env.DEV && raw === 'now' ? 'now' : '1021' }
export function stepWeight(): StepWeight { return mode }

/** 後ろ跳びの進み方 (0..1 → 0..1)。1021 は蹴った瞬間が一番速い (easeOutCubic)。今の版は等速。 */
export function backStepProgress(p: number): number {
  const t = Math.max(0, Math.min(1, p))
  return mode === '1021' ? 1 - (1 - t) ** 3 : t
}

export const STEP_WEIGHT = {
  crouchSquash: 0.06,   // 沈みの一番深い所 (足元を軸に縦を 6% 縮める)
  travelSquash: 0.035,  // 走る間の低さ
  landSquash: 0.075,    // 着いた瞬間の潰れ
  landPeakMs: 45,
  landRecoverMs: 190,
  backLandSquash: 0.06,
} as const

function landBump(ms: number): number {
  const S = STEP_WEIGHT
  if (ms < S.landPeakMs) return Math.sin((ms / S.landPeakMs) * Math.PI / 2)
  const r = (ms - S.landPeakMs) / S.landRecoverMs
  return r >= 1 ? 0 : (1 - r) ** 2
}

/** 足元を軸にした縦の倍率 (1 = 変えない)。描くだけ。 */
export function stepWeightSquashY(step: ExecutionerStepState, crouchMs: number): number {
  if (mode !== '1021') return 1
  const S = STEP_WEIGHT
  if (step.kind === 'back') {
    if (step.phase === 'travel') return 1 - S.travelSquash
    if (step.phase === 'land') return 1 - S.backLandSquash * landBump(step.elapsedMs)
    return 1
  }
  if (step.phase === 'crouch') {
    const p = crouchMs > 0 ? Math.min(1, step.elapsedMs / crouchMs) : 1
    return 1 - S.crouchSquash * p * p
  }
  if (step.phase === 'travel') return 1 - S.travelSquash
  if (step.phase === 'land') return 1 - Math.max(S.travelSquash * (1 - Math.min(1, step.elapsedMs / 60)), S.landSquash * landBump(step.elapsedMs))
  return 1
}

/** 蹴り出し・着地の土煙と、着地の滑りの擦り跡。描くだけ。 */
export function stepWeightDustFx(prev: StepHolder, next: StepHolder, now: number): FxEvent[] {
  if (mode !== '1021') return []
  const a = prev.exec?.step ?? null, b = next.exec?.step ?? null
  if (!b) return []
  const dir = b.toX < b.fromX ? -1 : 1
  const out: FxEvent[] = []
  if (b.phase === 'travel' && a?.phase !== 'travel') {
    // 蹴り出し: 後ろ足が床を蹴り、進む向きと逆へ土煙が舞う。
    out.push({ kind: 'knight_lunge_dust', variant: 'kick', x: b.fromX - dir * 20, y: next.y, t: now, dir, amount: b.kind === 'back' ? 0.55 : 0.75 })
  }
  if (b.phase === 'land' && a?.phase !== 'land') {
    // 着地: 前足が床を噛んで擦り、進む向きへ土煙。
    out.push({ kind: 'knight_lunge_dust', variant: 'scuff', x: b.toX - dir * 26, x2: b.toX + dir * 10, y: next.y, t: now, dir: -dir, amount: 0.6 })
    out.push({ kind: 'knight_lunge_dust', variant: 'kick', x: b.toX + dir * 8, y: next.y, t: now, dir: -dir, amount: 0.5 })
  }
  return out
}
